// The `evidence/library` COLLECTIONS read/write hooks — carved from
// `src/components/library/hooks/useCollections.ts`, which is deleted rather
// than left as a delegate (the no-shim ruling, 2026-10-08).
//
// WHAT STAYED BEHIND AND WHY IT IS NOT HERE. Two of the module's eight exports
// went to `/ui` instead of here, and the split is the headless rule rather
// than a judgement call: `useDeleteCollection` toasts through
// `@heidi/design-system/toast` and `useTranslations`, and
// `useDocumentCollectionsMap` colours its badges off `utils/collectionColors`,
// whose palette is typed by the design system's `BadgeColor`. Both live at
// `packages/evidence/src/ui/library/hooks/useCollections.ts`.
//
// S-enabled — THE GATE CROSSES THE SEAM, IT DOES NOT COLLAPSE INTO A
// PARAMETER. The three source-list reads took the one-boolean `enabled`
// severance at D2 slice 2 because they still had an app-side delegate to
// resolve it in. These do not: the old path is deleted, so there is nowhere
// app-side left to compute `isAuthenticated && IS_EVIDENCE_LIBRARY_ENABLED()`.
// Both terms arrive through `configureEvidenceData` instead — the verdict as a
// hook (ruling 65), the flag as a thunk — and the expression below is the
// legacy one term for term, in the legacy order.
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { logger } from '@heidi/runtime/logger';

import {
  COLLECTIONS_QUERY_KEY,
  COLLECTION_DETAIL_QUERY_KEY,
  type CollectionCreateRequest,
  type CollectionDetailResponse,
  type CollectionListResponse,
  type CollectionResponse,
  type CollectionUpdateRequest,
} from '../../../contracts';
import { isEvidenceLibraryEnabled, useIsAuthenticated } from '../../injected';
import {
  addDocumentsToCollection,
  createCollection,
  getCollection,
  listCollections,
  removeDocumentFromCollection,
  updateCollection,
} from '../api';

interface UseCollectionsOptions {
  /** Caller-level gate. Combines with the auth + evidence-library gates
   *  this hook already applies. Lets call sites that only care about
   *  collections under specific flag conditions (e.g. the source-
   *  recommendation banner under `release-unified-collections`) avoid
   *  spending a network round-trip when the data won't be used. */
  enabled?: boolean;
}

/**
 * Hook to fetch all collections accessible to the current user
 */
export function useCollections({ enabled = true }: UseCollectionsOptions = {}) {
  const isLibraryEnabled = isEvidenceLibraryEnabled();
  const isAuthenticated = useIsAuthenticated();
  return useQuery<CollectionListResponse>({
    queryKey: [COLLECTIONS_QUERY_KEY],
    queryFn: listCollections,
    enabled: isAuthenticated && isLibraryEnabled && enabled,
    // Keep the previously fetched page visible during refetches, including
    // when a refetch fails — this prevents the list from blanking out on
    // transient network errors. `isError` surfaces independently so callers
    // can render a retry affordance.
    placeholderData: keepPreviousData,
    // Override global 1-hour staleTime so collection counts stay fresh
    // (document_count updates when background jobs complete on the backend)
    staleTime: 30_000, // 30 seconds
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch a single collection with full details
 */
export function useCollection(collectionId: string | null) {
  const isLibraryEnabled = isEvidenceLibraryEnabled();
  const isAuthenticated = useIsAuthenticated();
  return useQuery<CollectionDetailResponse | undefined>({
    queryKey: [COLLECTION_DETAIL_QUERY_KEY, collectionId],
    queryFn: async () => {
      try {
        return await getCollection(collectionId!);
      } catch (error) {
        logger.error('Failed to fetch collection:', error);
        return undefined;
      }
    },
    enabled: isAuthenticated && !!collectionId && isLibraryEnabled,
  });
}

/**
 * Hook to create a new collection
 */
export function useCreateCollection() {
  const queryClient = useQueryClient();

  return useMutation<CollectionResponse, Error, CollectionCreateRequest>({
    mutationFn: createCollection,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] });
    },
  });
}

/**
 * Hook to update a collection
 */
export function useUpdateCollection() {
  const queryClient = useQueryClient();

  return useMutation<
    CollectionResponse,
    Error,
    { collectionId: string; data: CollectionUpdateRequest }
  >({
    mutationFn: ({ collectionId, data }) => updateCollection(collectionId, data),
    onSuccess: (_, { collectionId }) => {
      void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [COLLECTION_DETAIL_QUERY_KEY, collectionId],
      });
    },
  });
}

/**
 * Hook to add documents to a collection
 */
export function useAddDocumentsToCollection() {
  const queryClient = useQueryClient();

  return useMutation<
    CollectionResponse,
    Error,
    { collectionId: string; documentIds: string[] }
  >({
    mutationFn: ({ collectionId, documentIds }) =>
      addDocumentsToCollection(collectionId, documentIds),
    onSuccess: (_, { collectionId }) => {
      void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [COLLECTION_DETAIL_QUERY_KEY, collectionId],
      });
    },
  });
}

/**
 * Hook to remove a document from a collection
 */
export function useRemoveDocumentFromCollection() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { collectionId: string; documentId: string }>({
    mutationFn: ({ collectionId, documentId }) =>
      removeDocumentFromCollection(collectionId, documentId),
    onSuccess: (_, { collectionId }) => {
      void queryClient.invalidateQueries({ queryKey: [COLLECTIONS_QUERY_KEY] });
      void queryClient.invalidateQueries({
        queryKey: [COLLECTION_DETAIL_QUERY_KEY, collectionId],
      });
    },
  });
}
